import { useNavigate, useLocation } from 'react-router-dom';
import { userAppContext } from '../../../../context/Context';
import { UserAppContextType } from '../../../../router/types';
import { tagMapping } from '../helpers';
import type { Items } from '../../../../types';

type LinksExamplesProps = {
  searchItems?: Items[];
  setSearchItems?: (fn: (prev: Items[]) => Items[]) => void;
};

export const LinksExamples = ({ searchItems = [], setSearchItems }: LinksExamplesProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { restrictSources } = userAppContext() as unknown as UserAppContextType;

  const hasSelectedItems = searchItems && searchItems.length > 0;

  const handleTagClick = (item: Items) => {
    if (!setSearchItems) return;
    setSearchItems(prev => {
      const newItems = prev.filter(t => t.id !== item.id);
      if (newItems.length === 0) {
        navigate('/');
      }
      return newItems;
    });
    const params = new URLSearchParams(location.search);
    const idents = params.get('idents')?.split(',') ?? [];
    const newIdents = idents.filter(i => i !== item.id);
    if (newIdents.length > 0) {
      params.set('idents', newIdents.join(','));
    }
    navigate({ search: params.toString() });
  };

  const examplesData = [
    { label: 'Obesity', view: 'DISEASES', ident: 'C0028754', tab: 'GDA' },
    { label: 'LEPR', view: 'GENES', ident: '3953', tab: 'GDA' },
    { label: 'rs121913279', view: 'VARIANTS', ident: 'rs121913279', tab: 'VDA' },
    { label: 'Streptozocin', view: 'CHEMICALS', ident: 'CHEMBL1651906', tab: 'GDA' },
  ];

  const handleExampleClick = ({ view, ident, tab }: { view: string; ident: string; tab: string }) => {
    const source = restrictSources === 'CURATED' ? 'CURATED' : 'ALL';
    navigate({
      pathname: '/search',
      search: `?view=${view}&idents=${ident}&source=${source}&tab=${tab}`,
    });
  };

  return (
    <div className="mt-3 p-2 text-left search-examples">
      {hasSelectedItems ? (
        <>
          <span className="font-semibold">Selected:</span>
          {searchItems!.map((item, index) => (
            <span
              key={item.id ?? index}
              title={tagMapping(item).tooltip}
              className="example-responsive cursor-pointer"
              onClick={() => handleTagClick(item)}
              onKeyDown={e => e.key === 'Enter' && handleTagClick(item)}
              role="button"
              tabIndex={0}
              data-testid="searchTag"
            >
              {tagMapping(item).text} <i className="pl-1 bi-x-lg" aria-hidden />
            </span>
          ))}
        </>
      ) : (
        <>
          <span className="font-semibold">Examples:</span>
          {examplesData.map((example, index) => (
            <span
              key={index}
              className="example-responsive"
              onClick={() =>
                handleExampleClick({ view: example.view, ident: example.ident, tab: example.tab })
              }
            >
              {example.label} <i className="pl-1 bi-box-arrow-right" aria-hidden />
            </span>
          ))}
        </>
      )}
    </div>
  );
};
